import React from "react";
import { UseSetAsset } from "../../../api/calls/Queries";
import { QueryObserverResult } from "@tanstack/react-query";
import { Asset } from "../../../Types";
import reduceImage from "../../common/ImageReducer";

const GeneratedImagesGrid = (props: {
  isError: boolean;
  isPending: boolean;
  refetchDallE: (input: string) => Promise<any>;
  images: { index: number; url: string }[];
  setImages: React.Dispatch<
    React.SetStateAction<{ url: string; index: number }[] | undefined>
  >;
  prompt?: string;
  setSelectedAsset: React.Dispatch<
    React.SetStateAction<number | string | undefined>
  >;
  setPage: React.Dispatch<React.SetStateAction<1 | 2 | 3>>;
  refetch: () => Promise<QueryObserverResult<Asset[] | undefined>>;
}) => {
  const {
    refetch,
    setPage,
    setSelectedAsset: setOverallSelectedAsset,
    setImages,
    images,
    isError,
    isPending,
    refetchDallE,
    prompt,
  } = props;

  function goBack() {
    setImages(undefined);
    setOverallSelectedAsset(undefined);
  }

  const [selectedAsset, setSelectedAsset] = React.useState<
    number | undefined
  >();

  const { mutateAsync: uploadFile, isPending: isPendingUpload } =
    UseSetAsset(2);

  const selectedImage = React.useMemo(() => {
    return images.find((one) => one.index === selectedAsset);
  }, [images, selectedAsset]);

  const uploadToServer = React.useCallback(
    async (url: string, prompt: string) => {
      const fullResFile = await urlToFile(url, prompt);
      const lowResBlob = await generateImageThumb(url, prompt);

      const loweResFileName = prompt + "_thumbnail.png";

      const lowResFile = new File([lowResBlob], loweResFileName);

      uploadFile(
        { file: fullResFile, assetFileType: "F", assetId: 0 },
        {
          onSuccess: (firstUploadResp) => {
            if (lowResFile) {
              uploadFile(
                {
                  file: lowResFile,
                  assetFileType: "L",
                  assetId: firstUploadResp.assetId,
                },
                {
                  onSettled: () => {
                    refetch();
                    setPage(1);
                    setOverallSelectedAsset(firstUploadResp.assetId);
                    return;
                  },
                }
              );
            } else {
              refetch();
              setPage(1);
              setOverallSelectedAsset(firstUploadResp.assetId);
              return;
            }
          },
        }
      );
    },
    [uploadFile, refetch, setPage, setOverallSelectedAsset]
  );

  const isLoading: boolean = isPending || isPendingUpload;

  return (
    <div
      style={{
        // width: "50%",
        left: "50%",
        height: "100%",
        transform: "translate(-50%)",
        boxSizing: "border-box",
        display: "flex",
        position: "absolute",
        overflow: "hidden",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyItems: "center",
          gap: 10,
          alignItems: "center",
        }}
      >
        <div style={{ flexShrink: 0 }}>
          <button
            onClick={() => {
              goBack();
            }}
          >
            Indietro
          </button>
        </div>
        <div
          style={{
            flexGrow: 1,
            // textAlign: "center",
            // margin: 20,
            fontWeight: 600,
          }}
        >
          <span>{prompt}</span>
        </div>
      </div>
      <div
        className="dalle-image-grid"
        style={{
          opacity: isLoading ? 0.25 : 1,
          pointerEvents: isLoading ? "none" : "auto",
          cursor: isLoading ? "wait" : "pointer",
        }}
      >
        {images.map((img) => (
          <ImageElement
            key={img.index}
            image={img.url}
            onClick={() => setSelectedAsset(img.index)}
            selected={img.index === selectedAsset}
          />
        ))}
      </div>
      <div
        style={{
          width: "75%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <button
          id="dalle-grid-buttons"
          disabled={!prompt || isLoading}
          onClick={() => refetchDallE(prompt!)}
        >
          Genera di nuovo
        </button>
        <button
          id="dalle-grid-buttons"
          disabled={isLoading || !selectedAsset || !prompt}
          onClick={() => {
            if (selectedImage?.url && prompt) {
              uploadToServer(selectedImage.url, prompt);
            }
          }}
        >
          Carica immagine
        </button>
      </div>
      <pre style={{ visibility: isError ? "visible" : "hidden" }}>
        Errore API OpenAI
      </pre>

      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <div
          style={{
            width: 50,
            height: 50,
            visibility: !isLoading ? "hidden" : "visible",
            borderRightColor: "#10a37f",
            borderWidth: "6px",
          }}
          id="spinner"
        ></div>
      </div>
    </div>
  );
};

const ImageElement = (props: {
  image: string;
  onClick: () => void;
  selected?: boolean;
}) => {
  const { image, onClick, selected } = props;

  return (
    <div
      className="dalle-image-element"
      id={selected ? "selected" : undefined}
      onClick={() => {
        onClick();
      }}
    >
      <img src={image} alt="anteprima immagine generata" loading="lazy" />
    </div>
  );
};

export default GeneratedImagesGrid;

async function urlToFile(url: string, prompt: string) {
  const resp = await fetch(url, { mode: "no-cors", method: "GET" });

  const blob = await resp.blob();

  const name = "dalle_" + prompt.replace(/ /g, "_") + "_" + Date.now() + ".png";

  const file = new File([blob], name, { type: "png" });
  return file;
}

async function generateImageThumb(ev: string, prompt: string): Promise<File> {
  prompt = prompt.replace(/\s/g, "");

  const imageFetch = await fetch(ev);
  const imageBlob = await imageFetch.blob();

  const tempFile = new File([imageBlob], prompt + "_thumbnail.jpg", {
    type: "image/jpeg",
  });

  const resultCanvasThumb: HTMLCanvasElement = await reduceImage(
    tempFile,
    256,
    128
  );

  return new Promise((resolve) => {
    resultCanvasThumb.toBlob((blob) => {
      const file = new File([blob!], tempFile.name, { type: tempFile.type });
      resolve(file);
    });
  });
}

async function generateImageThumbnail(imageUrl: string): Promise<Blob> {
  const Img = new Image();

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  Img.src = imageUrl;

  return new Promise((resolve) => {
    Img.onload = () => {
      const ratio = Img.naturalWidth / Img.naturalHeight;
      let w;
      let h;
      if (ratio > 1) {
        w = 128;
        h = Math.floor(128 / ratio);
      } else {
        w = Math.floor(ratio * 128);
        h = 128;
      }
      canvas.width = ratio > 1 ? 128 : Math.floor(ratio * 128);
      canvas.height = ratio > 1 ? Math.floor(128 / ratio) : 128;

      if (ctx) {
        if (ctx?.imageSmoothingQuality === undefined) {
          ctx.filter = "blur(1px)";
        } else ctx.imageSmoothingQuality = "high";
        ctx.drawImage(Img, 0, 0, w, h);

        canvas.toBlob((thumbC) => {
          if (thumbC) {
            ctx.reset();

            resolve(thumbC);
          }
        });
      }
    };
  });
}
